/**
 * CORE DEFINITION dispatcher (2026-08-18) — the one category-aware entry the write
 * side (commands/library.ts) and both type-editor adapters call, so "which categories
 * have a declared core, and what does it lock" lives in exactly one place. Walls:
 * wallCore.ts (material always derived; thickness derived for the stud families).
 * Floors: floorCore.ts (material + thickness derived for the joist families;
 * concrete floors lock nothing — slab material stays a free choice).
 */
import type { AssemblyLayer, CategoryId, ParamValue } from './types'
import { conformWallCoreLayers, WALL_CORE_MATERIALS } from './wallCore'
import { conformFloorCoreLayers, FLOOR_CORE_MATERIALS } from './floorCore'
import { conformRoofCoreLayers, ROOF_CORE_MATERIALS } from './roofCore'
import { conformCeilingCoreLayers } from './ceilingCore'
import { ROOF_TILE_MATERIAL_NAMES, ROOF_TILE_MATERIAL_COLORS } from './roofTiles'

/** every canonical core material spec, by name — the conform pass creates these on
 *  demand in a doc that predates the families. The barrel-tile roofing LAYER
 *  material rides along: the roof finish-layer conform needs it creatable the same
 *  way (roofCore.ts#conformRoofCoreLayers). */
export const CORE_MATERIAL_SPECS: Record<string, {
  name: string; color: string; category: string
  cut?: { pattern: string; fgColor: string; bgColor: string }
}> = Object.fromEntries(
  [...Object.values(WALL_CORE_MATERIALS), ...Object.values(FLOOR_CORE_MATERIALS),
    ...Object.values(ROOF_CORE_MATERIALS),
    { name: ROOF_TILE_MATERIAL_NAMES.layer, color: ROOF_TILE_MATERIAL_COLORS[ROOF_TILE_MATERIAL_NAMES.layer], category: 'Roofing' }]
    .filter((s): s is NonNullable<typeof s> => !!s)
    .map((s) => [s.name, s]),
)

/** Does this category carry a core declaration? (raw `coreType` presence is still the
 *  per-record gate — see conformWallCore's undeclared-core guard) */
export const CORE_DECLARED_CATEGORIES: ReadonlySet<CategoryId> =
  new Set<CategoryId>(['wall', 'floor', 'roof', 'ceiling'])

/** Conform an assembly's core layer to the RESOLVED declaration — category dispatch. */
export function conformCoreLayersFor(
  category: CategoryId,
  params: Record<string, ParamValue>,
  layers: AssemblyLayer[],
  core: { start: number; end: number },
  materialIdByName: (name: string) => string | null,
): AssemblyLayer[] | null {
  if (category === 'wall') return conformWallCoreLayers(params, layers, core, materialIdByName)
  if (category === 'floor') return conformFloorCoreLayers(params, layers, core, materialIdByName)
  if (category === 'roof') return conformRoofCoreLayers(params, layers, core, materialIdByName)
  if (category === 'ceiling') return conformCeilingCoreLayers(params, layers, core, materialIdByName)
  return null
}

/**
 * What the LayerTable locks on the core row for this category + resolved params —
 * null = nothing locked (not a core-declared category, a concrete floor, or a
 * finish-only ceiling). `material` is implied true whenever a lock exists;
 * `thickness` locks only when the declared family derives it. Callers must ALSO
 * gate on a raw declaration (staged/stored `coreType` present) — an undeclared
 * record's layers are never locked or conformed against the schema default.
 */
export function coreLockFor(
  category: CategoryId,
  params: Record<string, ParamValue>,
): { thickness: boolean } | null {
  const ct = String(params.coreType ?? '')
  if (category === 'wall') return { thickness: ct === 'wood' || ct === 'metal' }
  if (category === 'floor' || category === 'ceiling') {
    return ct === 'wood' || ct === 'tji' ? { thickness: true } : null
  }
  if (category === 'roof') return { thickness: true }
  return null
}
